import { useCallback, useEffect, useMemo } from 'react'

import {
  Maybe,
  PaymentDetailFragment,
  PersonalInfoQuery,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { RequiredMembershipInfoBox } from '@/components/organisms'
import {
  handleError,
  useForm,
  useMessage,
  useRouter,
  useTranslation,
} from '@/core'
import { Template } from '@/core/architecture'

import {
  defaultValues,
  PersonalInfoForm,
  PersonalInfoFormProps,
  schema,
  SettingValues,
} from './PersonalInfoForm'

export * from './fragments'

export type State = {
  user: Maybe<PersonalInfoQuery['user']>
  paymentSource: Maybe<PaymentDetailFragment>
  isMembershipPersonalInformationRequired?: boolean
  submitting: boolean
  needVerifyPhoneNumber?: boolean
  sendingSMSCode?: boolean
  verifyingSMSCode?: boolean
}

export type Action = {
  onSubmit: PersonalInfoFormProps['onSubmit']
  onVerificationPhoneNumber: (values: { code: string }) => Promise<void>
  onSendVerificationSMS: () => Promise<void>
  onResendVerificationSMS: () => Promise<void>
  onChangeNeedVerifyPhoneNumber: (needVerifyPhoneNumber: boolean) => void
}

export const PersonalInfoSettingsTemplate: Template<State, Action> = ({
  state,
  onSubmit,
  onSendVerificationSMS,
  onResendVerificationSMS,
  onVerificationPhoneNumber,
  onChangeNeedVerifyPhoneNumber,
}) => {
  const { t } = useTranslation('personalInfoSettings')
  const { routerNames, routerPages } = useRouter()
  const { showError } = useMessage()

  const formContext = useMemo(
    () => ({
      hasFullName: !!state.user?.fullName,
      hasPhoneNumber: !!state.user?.phoneNumber,
      hasBirthday: !!state.user?.birthday,
      hasGender: !!state.user?.gender,
      hasPostalCode: !!state.user?.address?.postalCode,
      hasPrefecture: !!state.user?.address?.prefecture,
      hasCity: !!state.user?.address?.city,
      hasLine1: !!state.user?.address?.line1,
    }),
    [state.user]
  )

  const personalInfoForm = useForm<SettingValues>({
    schema,
    defaultValues,
    context: formContext,
  })

  const { reset } = personalInfoForm

  useEffect(() => {
    if (!state.user) return

    reset({
      birthday: state.user.birthday ?? null,
      gender: state.user.gender ?? null,
      phoneNumber: state.user.phoneNumber ?? '',
      fullName: state.user.fullName ?? '',
      postalCode: state.user.address?.postalCode ?? '',
      prefecture: state.user.address?.prefecture ?? '',
      city: state.user.address?.city ?? '',
      line1: state.user.address?.line1 ?? '',
      line2: state.user.address?.line2 ?? '',
      isMembershipPersonalInformationRequired:
        !!state.user?.isMembershipPersonalInformationRequired,
      ...formContext,
    })
  }, [state.user, reset, formContext])

  const handleSubmit = useCallback(
    async (values: SettingValues) => {
      await onSubmit(values).catch(e =>
        handleError<SettingValues>(e, personalInfoForm.setError, showError)
      )
    },
    [onSubmit, personalInfoForm, showError]
  )

  return (
    <BaseLayout
      margin
      header={{
        title: t('title'),
        defaultBackPage: routerPages.AccountSetting,
        back: true,
      }}
    >
      <Head title={routerNames.PersonalInfoSettings} />
      {state.isMembershipPersonalInformationRequired && (
        <RequiredMembershipInfoBox />
      )}
      <PersonalInfoForm
        visibleMoreInfo
        user={state.user}
        personalInfoForm={personalInfoForm}
        isMembershipPersonalInformationRequired={
          !!state.isMembershipPersonalInformationRequired
        }
        paymentSource={state.paymentSource}
        submitting={state.submitting}
        onSubmit={handleSubmit}
        needVerifyPhoneNumber={!!state.needVerifyPhoneNumber}
        sendingSMSCode={!!state.sendingSMSCode}
        verifyingSMSCode={!!state.verifyingSMSCode}
        onChangeNeedVerifyPhoneNumber={onChangeNeedVerifyPhoneNumber}
        onVerificationPhoneNumber={onVerificationPhoneNumber}
        onSendVerificationSMS={onSendVerificationSMS}
        onResendVerificationSMS={onResendVerificationSMS}
      />
    </BaseLayout>
  )
}
